import { useId } from "react";
import type { BudgetPeriod } from "./overviewFigures";
import styles from "./BudgetGauge.module.css";

/** The ring's radius and stroke, in the SVG's 120-unit box. */
const radius = 48;
const stroke = 11;
const circumference = 2 * Math.PI * radius;

/** The part of the ring the gauge uses: three quarters, open at the bottom, as a speedometer is. */
const sweep = 0.75;

/**
 * The length of the ring's arc for a share of the budget, held to the ring.
 *
 * @param percent - The share, as a percentage of the budget.
 * @returns The arc's length.
 */
function arc(percent: number): number {
  return (Math.min(100, Math.max(0, percent)) / 100) * circumference * sweep;
}

/**
 * One period's spend against its budget as a ring: what is spent as a glowing arc in the brand sweep, where the period is heading
 * as a dashed arc beyond it, and a tick for how much of the period has passed, so spend ahead of the tick is ahead of pace. Past
 * the budget, the projection turns to the warning colour. The figure in the middle is the share of the budget spent.
 *
 * @param props.period - The period's spend, projection, budget and time passed.
 * @param props.title - What the period is, above the figure, for example "October".
 * @param props.label - The ring's description for assistive technology: what it shows, in words.
 * @param props.caption - The budget, beneath the figure, for example "of $106,173".
 */
export function BudgetGauge({ period, title, label, caption }: { period: BudgetPeriod; title: string; label: string; caption: string }): React.JSX.Element {
  const gradientId = `budget-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const spentPercent = (period.spent / period.budget) * 100;
  const projectedPercent = period.projected === null ? null : (period.projected / period.budget) * 100;
  const over = projectedPercent !== null && projectedPercent > 100;
  // The ring starts at the bottom left (135°) and runs clockwise; the tick is placed along the same sweep.
  const tickAngle = (135 + Math.min(100, period.elapsedPercent) * 2.7) * (Math.PI / 180);
  const inner = radius - stroke / 2 - 3;
  const outer = radius + stroke / 2 + 3;

  return (
    <figure className={styles.gauge} role="img" aria-label={label}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--color-chart-blue)" />
            <stop offset="100%" stopColor="var(--color-chart-pink)" />
          </linearGradient>
        </defs>
        <g transform="rotate(135 60 60)">
          <circle className={styles.track} cx="60" cy="60" r={radius} strokeWidth={stroke} strokeDasharray={`${String(arc(100))} ${String(circumference)}`} />
          {projectedPercent !== null && projectedPercent > spentPercent && (
            <circle
              className={over ? `${styles.projected} ${styles.over}` : styles.projected}
              cx="60"
              cy="60"
              r={radius}
              strokeWidth={stroke}
              strokeDasharray={`${String(arc(projectedPercent))} ${String(circumference)}`}
            />
          )}
          <circle
            className={styles.spent}
            cx="60"
            cy="60"
            r={radius}
            strokeWidth={stroke}
            stroke={`url(#${gradientId})`}
            strokeDasharray={`${String(arc(spentPercent))} ${String(circumference)}`}
          />
        </g>
        <line
          className={styles.tick}
          x1={60 + inner * Math.cos(tickAngle)}
          y1={60 + inner * Math.sin(tickAngle)}
          x2={60 + outer * Math.cos(tickAngle)}
          y2={60 + outer * Math.sin(tickAngle)}
        />
      </svg>
      <div className={styles.centre}>
        <span className={styles.title}>{title}</span>
        <span className={over ? `${styles.value} ${styles.valueOver}` : styles.value}>{`${String(Math.round(spentPercent))}%`}</span>
        <span className={styles.caption}>{caption}</span>
      </div>
    </figure>
  );
}
